/* One theme, one key, one precedence — and both halves of it in one file.
 *
 * Six SPAs meant six keys (`sx-catalog-theme`, `waddle-theme`, the default `sx-theme`, …),
 * so a dark-mode choice made in Library was forgotten on the way to Training. One origin,
 * one key: the preference follows the operator everywhere, and the control no longer takes
 * a `storageKey` because there is nothing else it could be.
 *
 * The precedence is **stored → OS → dark** (the platform's dark-leaning operational
 * baseline: these surfaces are used at night, on station screens, for whole shifts).
 * `index.html` resolves exactly that, from this same key, in a pre-paint script — because a
 * module cannot run before the first paint and a theme applied in a React effect flashes a
 * white frame first. That script is the one place the key and the rule are restated, which
 * is why the control lives in this directory beside them rather than in a shared package:
 * change one, change both. The menu item itself is `ThemeMenuItem.tsx`, apart from this file
 * because the root route imports this one for the first paint, and a dropdown item would
 * drag Radix's whole menu into it.
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { flushSync } from "react-dom";
import { readBrowserValue, writeBrowserValue } from "../lib/browser-storage";

const CONSOLE_THEME_KEY = "theme";

function initialDark(): boolean {
  if (typeof window === "undefined") return true;
  const stored = readBrowserValue(
    { kind: "device" },
    CONSOLE_THEME_KEY,
    (value) => (value === "dark" || value === "light" ? value : undefined),
  );
  if (stored === "dark" || stored === "light") return stored === "dark";
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? true;
}

/** Where the flip was asked for, in viewport pixels: the new theme opens from there. */
interface ThemeToggleOrigin {
  readonly x: number;
  readonly y: number;
}

interface ConsoleTheme {
  readonly dark: boolean;
  readonly toggle: (origin?: ThemeToggleOrigin) => void;
}

const ThemeContext = createContext<ConsoleTheme | undefined>(undefined);

/* The theme is three facts on the document, applied together: the class Tailwind reads,
 * the stored preference, and the browser-chrome colour a station tablet paints. */
function applyTheme(dark: boolean): void {
  const root = document.documentElement;
  root.classList.toggle("dark", dark);
  // The canvas under the page, which index.html set before the stylesheet arrived.
  root.style.colorScheme = dark ? "dark" : "light";
  root.style.backgroundColor = dark ? "#16140f" : "#ffffff";
  writeBrowserValue(
    { kind: "device" },
    CONSOLE_THEME_KEY,
    dark ? "dark" : "light",
  );
  // The address-bar/status-bar colour is part of the theme on a station tablet, and
  // index.html set it pre-paint — keep the two in step when the operator flips it.
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", dark ? "#16140f" : "#ffffff");
}

/* The flip is one of the few whole-page changes the console makes, so it is one of the
 * few moments that earns memorable motion (DESIGN-POLICY §1): the new theme slides in as
 * a rectangle from the corner nearest the control that asked for it, through the View
 * Transitions API. The keyframes live in `styles.css` under `html[data-theme-transition]`.
 * Browsers without the API, and people who asked for reduced motion, get the instant
 * flip — the same three facts, applied in the same order. After beUI's theme toggle
 * (MIT), its "rectangle" variant. */
const THEME_TRANSITION_ATTRIBUTE = "data-theme-transition";
const THEME_FROM_PROPERTY = "--sx-theme-from";

/** The fully closed inset at the viewport corner nearest the origin: the reveal opens from it. */
export function revealFrom(
  origin: ThemeToggleOrigin | undefined,
  viewport: { readonly width: number; readonly height: number },
): string {
  const fromRight = origin === undefined || origin.x > viewport.width / 2;
  const fromBottom = origin !== undefined && origin.y > viewport.height / 2;
  // inset(top right bottom left): the two edges away from the corner are pushed to 100 %.
  return `inset(${fromBottom ? "100%" : "0"} ${fromRight ? "0" : "100%"} ${fromBottom ? "0" : "100%"} ${fromRight ? "100%" : "0"})`;
}

function flipTheme(
  next: boolean,
  origin: ThemeToggleOrigin | undefined,
  commit: () => void,
): void {
  const root = document.documentElement;
  const reduced =
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  if (reduced || typeof document.startViewTransition !== "function") {
    applyTheme(next);
    commit();
    return;
  }
  root.style.setProperty(
    THEME_FROM_PROPERTY,
    revealFrom(origin, { width: window.innerWidth, height: window.innerHeight }),
  );
  root.setAttribute(THEME_TRANSITION_ATTRIBUTE, "");
  const transition = document.startViewTransition(() => {
    applyTheme(next);
    commit();
  });
  // `finished` rejects when the transition is skipped (a second flip inside the first,
  // the tab hidden mid-reveal); the theme is applied either way, so both paths clean up
  // and neither rethrows.
  const settle = () => {
    root.removeAttribute(THEME_TRANSITION_ATTRIBUTE);
    root.style.removeProperty(THEME_FROM_PROPERTY);
  };
  transition.finished.then(settle, settle);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [dark, setDark] = useState(initialDark);
  const darkRef = useRef(dark);
  darkRef.current = dark;

  // Mount and any state change keep the document in step; `flipTheme` applies the same
  // facts synchronously inside a view transition, and this effect then finds them true.
  useEffect(() => {
    applyTheme(dark);
  }, [dark]);

  const toggle = (origin?: ThemeToggleOrigin) => {
    const next = !darkRef.current;
    flipTheme(next, origin, () => flushSync(() => setDark(next)));
  };

  return (
    <ThemeContext.Provider value={{ dark, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useConsoleTheme(): ConsoleTheme {
  const theme = useContext(ThemeContext);
  if (theme === undefined) {
    throw new Error("useConsoleTheme must be used inside ThemeProvider");
  }
  return theme;
}
